import { Button, Modal, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { showErrorNotifications } from "../../utils/handlers/handle-error"
import { syncProposalToLead } from "./api/services.ts"
import { ComparisonTable, type ComparisonTableColumn } from "./ComparisonTable.tsx"
import type { FieldComparison } from "./types.ts"

const { Text, Title } = Typography

interface ProposalGenerateConfirmModalProps {
  open: boolean
  onClose: () => void
  /**
   * The changed-fields list to show — callers filter to `has_diff` rows
   * before passing these in (see `DocumentProposalEditPage.handleSaveAndUpdate`).
   * `sales_owner` is filtered again here, defensively, so it can never appear
   * in this modal even if a caller forgets to strip it.
   */
  diffs: FieldComparison[]
  proposalId: string
  /** `document_label` from the same `getProposalSyncStatus` call that
   * produced `diffs` — confirmed live as a top-level field alongside
   * `fields`/`diff_count`, e.g. "Proposal · v3". Falls back to the bare
   * "Proposal" when absent (an older cached response, or a call site that
   * hasn't threaded it through). */
  documentLabel?: string
  /**
   * Finishes the Generate action (save-file + navigate) — called once the
   * user has made their one decision, whichever way it went. Never called
   * from a plain close/dismiss.
   */
  onProceed: () => void | Promise<void>
}

/**
 * Shown only when Generate/Update & Generate would otherwise leave the Lead
 * out of sync with edits just made on this Proposal. One decision for every
 * changed field at once — no per-field checkboxes, no "canonical field" or
 * "source priority" language. The Proposal's own edits are already saved
 * (the PATCH that runs before this modal opens) regardless of what's chosen
 * here; a sync write only ever lands on the Lead.
 */
function ProposalGenerateConfirmModal({
  open,
  onClose,
  diffs,
  proposalId,
  documentLabel,
  onProceed
}: ProposalGenerateConfirmModalProps) {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)

  const visibleDiffs = diffs.filter((diff) => diff.key !== "sales_owner")

  const columns: ComparisonTableColumn[] = [
    { key: "lead", label: t("documentSync.proposalGenerateConfirm.leadColumn") },
    {
      key: "proposal",
      label:
        documentLabel ||
        t("documentSync.proposalGenerateConfirm.proposalColumn")
    }
  ]

  const handleKeepLeadAsIs = () => {
    onClose()
    void onProceed()
  }

  const handleUpdateLead = async () => {
    try {
      setSaving(true)

      const res = await syncProposalToLead(
        proposalId,
        visibleDiffs.map((diff) => diff.key)
      )
      if (!res.ok) {
        throw new Error(t("documentSync.proposalGenerateConfirm.updateLeadFailed"))
      }

      onClose()
      await onProceed()
    } catch (e) {
      showErrorNotifications(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onCancel={saving ? undefined : onClose}
      maskClosable={false}
      centered
      width={680}
      title={
        <Title level={4} style={{ margin: 0 }}>
          {t("documentSync.proposalGenerateConfirm.title")}
        </Title>
      }
      footer={[
        <Button key={"keep"} disabled={saving} onClick={handleKeepLeadAsIs}>
          {t("documentSync.proposalGenerateConfirm.keepLeadAsIs")}
        </Button>,
        <Button
          key={"update"}
          type={"primary"}
          loading={saving}
          onClick={handleUpdateLead}
        >
          {t("documentSync.proposalGenerateConfirm.updateLead")}
        </Button>
      ]}
    >
      <Text>
        {t("documentSync.proposalGenerateConfirm.itemsChanged", {
          count: visibleDiffs.length
        })}
      </Text>

      <div className={"my-4"} data-testid={"proposal-generate-confirm-fields"}>
        <ComparisonTable
          columns={columns}
          testIdPrefix={"proposal-generate-confirm"}
          rows={visibleDiffs.map((diff) => ({
            key: diff.key,
            label: diff.label,
            hasDiff: diff.has_diff,
            values: {
              lead: diff.lead_display ?? "—",
              proposal:
                diff.proposal_display ??
                t("documentSync.proposalGenerateConfirm.notSet")
            }
          }))}
        />
      </div>

      <Text type={"secondary"}>
        {t("documentSync.proposalGenerateConfirm.updateQuestion")}
      </Text>
    </Modal>
  )
}

export { ProposalGenerateConfirmModal }
